import { useEffect, useMemo, type ReactElement } from 'react';
import { Alert, Box, Group, Loader, Stack, Text } from '@mantine/core';
import { AlertTriangle, UserRoundCheck } from 'lucide-react';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import PageLevelFilterRow from '@/shared/surfaces/PageLevelFilterRow';
import SideRailPageLayout from '@/shared/surfaces/SideRailPageLayout';
import TopicSummaryPanel from '@/shared/surfaces/TopicSummaryPanel';
import InsightsRailCard from '@/shared/surfaces/InsightsRailCard';
import type { StatItem } from '@/design/StatBand';
import type { VizAccent } from '@/design/VizAccents';
import SurfaceAssistScope, { SurfaceAssistToolbar } from '@/shared/surfaces/assist/SurfaceAssistScope';
import { useSurface } from '@/shared/surfaces/useSurface';
import TopicNotFound from '@/shared/surfaces/TopicNotFound';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { useGlobalFilters } from '@/features/global-filters/useGlobalFilters';
import { formatCount, formatUsd } from '@/shared/analytics/MetricFormatting';
import { LicenseFitViewModel } from './LicenseFitViewModel';
import LicenseFitTable from './LicenseFitTable';

/** The License Fit screen's accent — shared by the header and rail Insights. */
const LICENSE_FIT_ACCENT: VizAccent = 'violet';

/** Spend › Cost › License Fit: per-user seat-vs-API footing — who's overpaying for a seat and who'd be cheaper on one. */
export default function SpendLicenseFitPage(): ReactElement {
    const vm = useViewModel(LicenseFitViewModel);
    const surface = useSurface('token-usage');
    const { snapshot } = useGlobalFilters();
    const filterKey = useMemo(() => JSON.stringify(snapshot), [snapshot]);

    useEffect(() => {
        void vm.reload();
    }, [vm, filterKey]);

    if (surface === undefined) return <TopicNotFound />;

    const summary = vm.result?.summary ?? null;
    const stats: StatItem[] =
        summary === null ?
            []
        :   [
                { label: 'Wasted seat spend', value: formatUsd(summary.wastedSeatUsd), hint: `${formatCount(summary.reclaimCandidates)} reclaim candidates` },
                { label: 'License value', value: formatUsd(summary.licenseValueUsd), hint: 'seats cheaper than metering' },
                { label: 'API → seat savings', value: formatUsd(summary.apiToSeatUsd), hint: 'heavy metered users' },
                { label: 'Net opportunity', value: formatUsd(summary.netOpportunityUsd) },
            ];
    const hasRows = vm.result !== null && vm.result.rows.length > 0;

    return (
        <SurfaceAssistScope surface={surface}>
            <Box pb="xl">
                <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

                <PageHeaderComponent
                    icon={UserRoundCheck}
                    eyebrow="Cost"
                    title="License Fit"
                    subtitle="Per-user seat-vs-API footing: who's overpaying for an underused seat and who'd be cheaper on a license."
                />

                <Box mt="lg">
                    {vm.loading && vm.result === null ?
                        <Group justify="center" py={64}>
                            <Loader size="sm" />
                        </Group>
                    : vm.error !== null ?
                        <Alert color="red" icon={<AlertTriangle size={16} />} title="Could not load License Fit">
                            {vm.error}
                        </Alert>
                    : !hasRows ?
                        <Text size="sm" c="dimmed">
                            No seat assignments or metered usage this month.
                        </Text>
                    :   <SideRailPageLayout
                            rail={
                                <Stack gap="md">
                                    <TopicSummaryPanel stats={stats} />
                                    <InsightsRailCard derived={vm.callouts} accent={LICENSE_FIT_ACCENT} />
                                    <Text size="xs" c="dimmed">
                                        Forecast projects this month&apos;s usage to month-end on a working-day run rate, priced at the observed fleet rate per model; the verdict compares that against the seat price (the user&apos;s seat, or the cheapest available seat for metered users).
                                    </Text>
                                </Stack>
                            }
                        >
                            <Stack gap="sm">
                                <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
                                    Per-user footing
                                </Text>
                                <LicenseFitTable rows={vm.result?.rows ?? []} />
                            </Stack>
                        </SideRailPageLayout>
                    }
                </Box>
            </Box>
        </SurfaceAssistScope>
    );
}
